/**
 * Rasterizer abstraction for the HTML export pipeline.
 *
 * A RasterSource is one *capture* of a DOM subtree that can be drawn to
 * canvases per-REGION — pages pull their own strip instead of the pipeline
 * ever materializing a single full-height canvas (which hits browser
 * dimension/area caps on tall documents).
 *
 * All coordinates are CSS px; `scale` maps CSS px → device px at draw time.
 */

export type RasterEngineName = 'snapdom' | 'html2canvas-pro'

export interface RasterizeRequest {
  /** Root to capture (the capture-session iframe's documentElement, or a bg layer div). */
  el: HTMLElement
  /** Layout width of the capture, CSS px. */
  widthCss: number
  /** Layout height of the capture, CSS px. */
  heightCss: number
}

/** A rectangle in CSS px within the captured content. */
export interface Region {
  x: number
  y: number
  w: number
  h: number
}

export interface RasterSource {
  readonly widthCss: number
  readonly heightCss: number
  readonly engine: RasterEngineName
  /** Render one region at the given scale. Never exceeds one page's canvas. */
  toCanvas(region: Region, scale: number): Promise<HTMLCanvasElement>
  dispose(): void
}

export interface RasterEngine {
  readonly name: RasterEngineName
  capture(req: RasterizeRequest): Promise<RasterSource>
}

/** Browser-safe per-dimension canvas cap (Chrome 65535; Safari lower — stay well under). */
export const MAX_CANVAS_DIM = 16384
/** Above this content height (CSS px) the snapdom SVG-image path may fail to
 *  decode on some browsers — fall back to the regioned html2canvas engine. */
export const MAX_SOURCE_HEIGHT = 30_000
/** Hard time budget for a single engine capture. */
export const CAPTURE_TIMEOUT_MS = 20_000
